/**
 * Feature Management (Admin) — grants granular per-employer control over
 * which features/pages/sections/actions are visible in the product.
 *
 * Three panes on one page:
 *   · LEFT   — Companies list. Click a company to edit its entitlements.
 *   · MIDDLE — Preset picker + Overrides toggle table (grouped by category).
 *   · RIGHT  — Live preview of the resolved feature map, updated on the fly.
 *
 * Presets pane (bottom): manage the shared preset library — create, clone,
 * rename, edit, or delete non-system presets.
 *
 * Everything is admin-only. Endpoints: /api/features/registry, /presets,
 * /companies, /companies/{id}, /preview.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useFeatures } from "@/context/FeaturesContext";
import { toast } from "sonner";
import { Loader2, Save, Copy, Trash2, Eye, ChevronRight, ChevronDown, Search } from "lucide-react";

export default function FeatureManagement() {
    const [searchParams] = useSearchParams();
    const initialCid = searchParams.get("cid");
    const [registry, setRegistry] = useState(null);   // { rows, by_category, by_page }
    const [presets, setPresets] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [companyQuery, setCompanyQuery] = useState("");
    const [selectedCid, setSelectedCid] = useState(initialCid || null);
    const [companyState, setCompanyState] = useState(null); // { preset_key, overrides, resolved, company }
    const [previewResolved, setPreviewResolved] = useState(null);
    // "Preview as employer" mode — when on, we swap the whole app's feature
    // map to the currently-selected company's resolved features so the admin
    // can eyeball what THAT employer sees, without impersonating the account.
    // The banner + off switch always give a way out.
    const { setImpersonation, impersonating } = useFeatures();
    const [saving, setSaving] = useState(false);
    const [loadingCompany, setLoadingCompany] = useState(false);

    // Initial load — registry, presets, companies list — in parallel.
    useEffect(() => {
        (async () => {
            try {
                const [r, p, c] = await Promise.all([
                    api.get("/features/registry"),
                    api.get("/features/presets"),
                    api.get("/features/companies"),
                ]);
                setRegistry(r.data);
                setPresets(p.data.presets || []);
                setCompanies(c.data.companies || []);
                if (c.data.companies?.length && !selectedCid) setSelectedCid(c.data.companies[0].id);
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, []);

    // When the selected company changes, load its resolved features + overrides.
    useEffect(() => {
        if (!selectedCid) return;
        setLoadingCompany(true);
        (async () => {
            try {
                const { data } = await api.get(`/features/companies/${selectedCid}`);
                setCompanyState(data);
                setPreviewResolved(data.resolved);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoadingCompany(false);
            }
        })();
    }, [selectedCid]);

    // Compute an updated preview whenever preset or overrides change.
    const computePreview = useCallback(async (preset_key, overrides) => {
        try {
            const { data } = await api.post("/features/preview", { preset_key, overrides });
            setPreviewResolved(data.resolved);
        } catch (e) { /* silent — preview is best-effort */ }
    }, []);

    const onPresetChange = (key) => {
        setCompanyState((s) => ({ ...s, preset_key: key }));
        computePreview(key, companyState?.overrides || {});
    };

    const onToggleOverride = (fid) => {
        setCompanyState((s) => {
            const current = s?.overrides || {};
            // Tri-state cycle: [not set] → false → true → [not set]
            const isSet = Object.prototype.hasOwnProperty.call(current, fid);
            const next = { ...current };
            if (!isSet) next[fid] = false;
            else if (current[fid] === false) next[fid] = true;
            else delete next[fid];
            computePreview(s.preset_key, next);
            return { ...s, overrides: next };
        });
    };

    const onSave = async () => {
        if (!selectedCid || !companyState) return;
        setSaving(true);
        try {
            const { data } = await api.put(`/features/companies/${selectedCid}`, {
                preset_key: companyState.preset_key || null,
                overrides: companyState.overrides || {},
            });
            setPreviewResolved(data.resolved);
            toast.success("Features saved — the employer sees the new setup on their next reload");
            // Refresh the companies list so preset_name + override_count stay accurate.
            const { data: cs } = await api.get("/features/companies");
            setCompanies(cs.companies || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const enabledCount = useMemo(() => previewResolved ? Object.values(previewResolved).filter(Boolean).length : 0, [previewResolved]);

    return (
        <div className="p-4 sm:p-6" data-testid="feature-management-page">
            <header className="mb-4 pb-4 border-b border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Super admin</div>
                <h1 className="font-display font-black text-3xl text-ikb leading-tight">Feature Management</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Choose a company, pick a preset, then fine-tune individual features. Presets set the baseline; overrides flip specific features. Employers see the resolved map on next page load.
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_320px] gap-4">
                {/* LEFT — company list */}
                <aside className="border border-border bg-white max-h-[70vh] overflow-auto">
                    <div className="px-3 py-2 border-b border-border bg-neutral-50 text-[10px] font-mono uppercase tracking-widest">Employers · {companies.length}</div>
                    <div className="px-2 py-2 border-b border-border bg-white sticky top-0 z-10">
                        <label className="relative block">
                            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <input
                                type="text"
                                value={companyQuery}
                                onChange={(e) => setCompanyQuery(e.target.value)}
                                placeholder="Search employers…"
                                className="h-8 w-full border border-border bg-white text-xs pl-7 pr-2"
                                data-testid="fm-employer-search"
                            />
                        </label>
                    </div>
                    <ul>
                        {companies
                            .filter((c) => {
                                const q = companyQuery.trim().toLowerCase();
                                if (!q) return true;
                                return (c.name || "").toLowerCase().includes(q) || (c.preset_name || "").toLowerCase().includes(q);
                            })
                            .map((c) => (
                            <li key={c.id}>
                                <button
                                    type="button"
                                    onClick={() => setSelectedCid(c.id)}
                                    className={`w-full text-left px-3 py-2 border-b border-border hover:bg-secondary/40 ${selectedCid === c.id ? "bg-ikb/5 border-l-4 border-l-ikb" : ""}`}
                                    data-testid={`fm-company-${c.id}`}
                                >
                                    <div className="font-semibold text-sm truncate">{c.name}</div>
                                    <div className="text-[10px] font-mono text-muted-foreground">
                                        {c.preset_name || "No preset"} · {c.override_count} overrides
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* MIDDLE — preset + overrides editor */}
                <section className="border border-border bg-white">
                    <div className="px-4 py-3 border-b border-border bg-neutral-50 flex items-center gap-3 flex-wrap">
                        <div className="text-[10px] font-mono uppercase tracking-widest">Configuration for</div>
                        <div className="font-bold text-sm">{companyState?.company?.name || "…"}</div>
                        <div className="ml-auto flex items-center gap-2">
                            {companyState && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await setImpersonation({
                                                company_id: companyState.company?.id,
                                                company_name: companyState.company?.name,
                                            });
                                            toast.success(`Now viewing as ${companyState.company?.name} (read-only)`);
                                        } catch (e) {
                                            toast.error(formatApiError(e));
                                        }
                                    }}
                                    className="h-8 px-3 border border-amber-500 text-amber-700 bg-white text-[11px] font-mono uppercase tracking-widest hover:bg-amber-50 inline-flex items-center gap-1.5"
                                    data-testid="fm-preview-as"
                                    disabled={impersonating}
                                    title={impersonating ? "Exit current preview first" : "View the whole app as this employer sees it (read-only)"}
                                >
                                    <Eye className="w-3.5 h-3.5" /> Impersonate employer
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onSave}
                                disabled={saving || !companyState}
                                className="h-8 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1.5 disabled:opacity-60"
                                data-testid="fm-save"
                            >
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                Save
                            </button>
                        </div>
                    </div>

                    {loadingCompany ? (
                        <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                    ) : companyState ? (
                        <div className="p-4 space-y-4">
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Preset (baseline)</label>
                                <select
                                    value={companyState.preset_key || ""}
                                    onChange={(e) => onPresetChange(e.target.value || null)}
                                    className="mt-1 h-9 w-full border border-border bg-white text-sm px-2"
                                    data-testid="fm-preset-select"
                                >
                                    <option value="">— No preset (registry defaults) —</option>
                                    {presets.map((p) => (
                                        <option key={p.key} value={p.key}>
                                            {p.name}{p.is_system ? " · system" : ""}
                                        </option>
                                    ))}
                                </select>
                                {companyState.preset_key && (
                                    <div className="text-[11px] text-muted-foreground mt-1">
                                        {presets.find(p => p.key === companyState.preset_key)?.description}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                                    Overrides · click a pill to cycle: baseline → force-off → force-on → baseline
                                </div>
                                {registry?.by_page?.map((pageGroup) => (
                                    <PageGroup
                                        key={pageGroup.page}
                                        pageGroup={pageGroup}
                                        overrides={companyState.overrides || {}}
                                        previewResolved={previewResolved || {}}
                                        onToggle={onToggleOverride}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="p-6 text-center text-sm text-muted-foreground">Pick a company on the left to start.</div>
                    )}
                </section>

                {/* RIGHT — resolved preview */}
                <aside className="border border-border bg-white h-fit sticky top-4">
                    <div className="px-3 py-2 border-b border-border bg-neutral-50 flex items-center gap-2">
                        <Eye className="w-3.5 h-3.5 text-ikb" />
                        <span className="text-[10px] font-mono uppercase tracking-widest">Live preview</span>
                    </div>
                    <div className="p-4">
                        <div className="text-xs text-muted-foreground">Resolved (after preset + overrides):</div>
                        <div className="mt-1 font-display font-black text-3xl text-ikb tabular-nums" data-testid="fm-enabled-count">
                            {enabledCount} <span className="text-sm text-muted-foreground font-normal">/ {registry?.rows?.length || 0} on</span>
                        </div>
                        <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                            Currently disabled ({(registry?.rows?.length || 0) - enabledCount})
                        </div>
                        <ul className="mt-1 space-y-0.5 max-h-96 overflow-auto pr-1">
                            {registry?.rows?.filter((r) => !previewResolved?.[r.id]).map((r) => (
                                <li key={r.id} className="text-[11px] font-mono text-rose-700 truncate">
                                    <ChevronRight className="w-2.5 h-2.5 inline" /> {r.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                </aside>
            </div>

            {/* PRESETS LIBRARY */}
            <PresetsLibrary
                presets={presets}
                onChanged={async () => {
                    const { data } = await api.get("/features/presets");
                    setPresets(data.presets || []);
                }}
                registry={registry}
            />
        </div>
    );
}

/** Collapsible Page group — renders a top-level heading (e.g. "Candidates")
 *  with nested Section rows underneath. Defaults to expanded; the admin
 *  can collapse rarely-touched pages to focus on one area at a time. */
function PageGroup({ pageGroup, overrides, previewResolved, onToggle }) {
    const [open, setOpen] = useState(true);
    const total = pageGroup.sections.reduce((n, s) => n + s.features.length, 0);
    const overrideCount = pageGroup.sections.reduce(
        (n, s) => n + s.features.filter((f) => Object.prototype.hasOwnProperty.call(overrides, f.id)).length,
        0,
    );
    const CATEGORY_LABEL = { "nav": "nav", "page-section": "section", "action": "button", "integration": "integration" };
    return (
        <div className="mb-4 border border-border" data-testid={`fm-page-${pageGroup.page}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-ikb/5 hover:bg-ikb/10 text-left"
            >
                {open ? <ChevronDown className="w-3.5 h-3.5 text-ikb" /> : <ChevronRight className="w-3.5 h-3.5 text-ikb" />}
                <span className="font-display font-black text-sm text-ikb">{pageGroup.page}</span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground ml-auto">
                    {total} features · {overrideCount} overrides
                </span>
            </button>
            {open && (
                <div className="divide-y divide-border">
                    {pageGroup.sections.map((sec) => (
                        <SectionGroup
                            key={sec.section}
                            section={sec}
                            overrides={overrides}
                            previewResolved={previewResolved}
                            onToggle={onToggle}
                            categoryLabel={CATEGORY_LABEL}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/** Section — a sub-group inside a Page (e.g. "Views", "AI actions").
 *  Rendered as a small header with the feature rows below it. */
function SectionGroup({ section, overrides, previewResolved, onToggle, categoryLabel }) {
    const [open, setOpen] = useState(true);
    return (
        <div>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 px-3 py-1.5 bg-neutral-50 hover:bg-neutral-100 text-left"
            >
                {open ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{section.section}</span>
                <span className="text-[10px] text-muted-foreground ml-auto">{section.features.length}</span>
            </button>
            {open && (
                <ul className="divide-y divide-border">
                    {section.features.map((row) => {
                        const ovVal = overrides?.[row.id];
                        const isSet = Object.prototype.hasOwnProperty.call(overrides || {}, row.id);
                        const resolvedOn = !!previewResolved?.[row.id];
                        const pillCls = !isSet ? "border-border text-muted-foreground bg-neutral-50"
                            : ovVal === true ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                            : "border-rose-400 text-rose-700 bg-rose-50";
                        const pillText = !isSet ? "auto" : ovVal ? "force ON" : "force OFF";
                        return (
                            <li key={row.id} className="flex items-center gap-3 pl-8 pr-3 py-2 text-xs">
                                <button
                                    type="button"
                                    onClick={() => onToggle(row.id)}
                                    className={`shrink-0 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${pillCls} min-w-[68px] text-center`}
                                    data-testid={`fm-override-${row.id}`}
                                    title="Click to cycle: baseline → force-off → force-on"
                                >
                                    {pillText}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-xs truncate">{row.label}</div>
                                    <div className="text-[10px] text-muted-foreground truncate">{row.description}</div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border border-neutral-300 text-neutral-600 bg-white">
                                        {categoryLabel[row.category] || row.category}
                                    </span>
                                    {row.is_advanced && (
                                        <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border border-amber-300 text-amber-700 bg-amber-50">adv</span>
                                    )}
                                    {row.is_proprietary && (
                                        <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border border-rose-300 text-rose-700 bg-rose-50">prop</span>
                                    )}
                                    <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${resolvedOn ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-neutral-300 text-neutral-500 bg-neutral-50"}`}>
                                        {resolvedOn ? "ON" : "OFF"}
                                    </span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/** Library — list presets + clone / delete + inline features view (read-only for system). */
function PresetsLibrary({ presets, onChanged, registry }) {
    const [busy, setBusy] = useState(null);
    async function clone(key) {
        setBusy(key);
        try { await api.post(`/features/presets/${key}/clone`); toast.success("Preset cloned"); onChanged(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    }
    async function del(key) {
        if (!window.confirm(`Delete preset '${key}'? Companies using it must be reassigned first.`)) return;
        setBusy(key);
        try { await api.delete(`/features/presets/${key}`); toast.success("Deleted"); onChanged(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    }
    return (
        <section className="mt-6 border border-border bg-white" data-testid="fm-presets-library">
            <div className="px-4 py-2.5 border-b border-border bg-neutral-50 flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase tracking-widest">Preset library · {presets.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 divide-x divide-border">
                {presets.map((p) => {
                    const on = Object.values(p.features || {}).filter(Boolean).length;
                    const total = registry?.rows?.length || 0;
                    return (
                        <div key={p.key} className="p-4 space-y-2" data-testid={`fm-preset-${p.key}`}>
                            <div className="flex items-center gap-2">
                                <div className="font-bold text-sm">{p.name}</div>
                                {p.is_system && <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border border-blue-300 text-blue-700 bg-blue-50">system</span>}
                            </div>
                            <div className="text-[11px] text-muted-foreground line-clamp-3">{p.description}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">key: <span className="text-foreground">{p.key}</span> · {on}/{total} on</div>
                            <div className="flex items-center gap-1.5 pt-1">
                                <button
                                    type="button"
                                    onClick={() => clone(p.key)}
                                    disabled={busy === p.key}
                                    className="inline-flex items-center gap-1 h-7 px-2 border border-border bg-white hover:bg-neutral-50 text-[10px] font-mono uppercase tracking-widest disabled:opacity-60"
                                    data-testid={`fm-clone-${p.key}`}
                                >
                                    <Copy className="w-3 h-3" /> Clone
                                </button>
                                {!p.is_system && (
                                    <button
                                        type="button"
                                        onClick={() => del(p.key)}
                                        disabled={busy === p.key}
                                        className="inline-flex items-center gap-1 h-7 px-2 border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 text-[10px] font-mono uppercase tracking-widest disabled:opacity-60"
                                        data-testid={`fm-delete-${p.key}`}
                                    >
                                        <Trash2 className="w-3 h-3" /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
